import React, { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Card, Field, Badge, SectionTitle } from "@/components/ui-hud";
import { api } from "@/lib/api";
import { toast } from "sonner";
import FileUpload from "@/components/FileUpload";
import AirportInput from "@/components/AirportInput";
import PasswordInput from "@/components/PasswordInput";
import { Plane, Building2, ShieldCheck, ArrowRight, ArrowLeft, CheckCircle2, Loader2, Trash2, Edit3 } from "lucide-react";

const STEPS = ["Company", "Certification & D085", "Fleet", "Coverage & Safety", "Review"];

const RATINGS = ["None", "PJP", "ARGUS Gold", "ARGUS Gold Plus", "ARGUS Platinum", "WYVERN Wingman", "IS-BAO"];
const HIRING = ["Available for charter requests", "Not available"];
const SERVICE_REGIONS = [
  "North America",
  "South America",
  "Canada",
  "Mexico",
  "Bahamas",
  "Caribbean",
  "US / British Virgin Islands",
  "Cayman Islands",
  "Europe",
  "Asia",
  "World Wide Ops",
];

export default function OperatorOnboard() {
  const { user, refresh } = useAuth();
  const nav = useNavigate();
  const [params] = useSearchParams();
  const initialStep = params.get("step") === "fleet" ? 2
    : params.get("step") === "certification" ? 1
    : params.get("step") === "coverage" ? 3
    : params.get("step") === "review" ? 4
    : 0;
  const [step, setStep] = useState(initialStep);
  const [catalog, setCatalog] = useState({});
  const [fleet, setFleet] = useState([]);
  const [busy, setBusy] = useState(false);
  const editAircraftId = params.get("edit") || null;
  const newAircraft = params.get("new") === "1";
  const [profile, setProfile] = useState({
    company_name: "",
    part_135_certificate: "",
    faa_operator_id: "",
    d085_file_id: "",
    company_logo_file_id: "",
    hero_photo_file_id: "",
    primary_bases: [],
    service_regions: [],
    hiring_status: "Available for charter requests",
    argus_wyvern_rating: "None",
    contact_email: user?.email || "",
    contact_phone: "",
  });

  useEffect(() => {
    api.get("/faa/type-ratings/catalog").then((r) => setCatalog(r.data || {})).catch(() => {});
    if (user?.role === "charter_operator") {
      api.get("/operators/me/fleet").then((r) => setFleet(r.data.fleet || [])).catch(() => {});
      const rp = user.role_profile || {};
      setProfile((p) => ({ ...p, ...rp, contact_email: rp.contact_email || user.email || "" }));
    }
  }, [user?.id]);

  if (!user) {
    return <OperatorEntitySignup onDone={() => nav(0)} />;
  }
  if (user.role !== "charter_operator") {
    return (
      <div className="max-w-3xl mx-auto px-6 py-16 space-y-8">
        <Card>
          <div className="overline text-orange-hud mb-2">// A COMPANY ACCOUNT IS SEPARATE</div>
          <h1 className="font-display text-3xl font-bold tracking-tight mb-3">Your charter company needs its own PJP account.</h1>
          <p className="text-sm text-white/60 mb-2">
            You're signed in as <span className="text-white/85">{user.first_name} {user.last_name}</span> ({(user.role || "").replace(/_/g," ")}).
            Business entities — <span className="text-cyan mono">Charter Operators & FBOs</span> — live on their own accounts so
            the ops cert, ARGUS/Wyvern rating, fleet, and Acrisure paperwork stay cleanly separated from your personal PHF.
          </p>
          <p className="text-sm text-white/60 mb-6">
            Create the company account below (use your company email, e.g. <span className="mono text-cyan">JMC@FlyIntegra.com</span>).
            You can log in and out between your personal and business accounts anytime.
          </p>
        </Card>
        <OperatorEntitySignup onDone={() => nav(0)} />
      </div>
    );
  }

  const next = () => {
    if (step === 0 && !profile.company_name) return toast.error("Company name required");
    if (step === 1 && !profile.part_135_certificate) return toast.error("Part 135 Certificate # required");
    setStep((s) => Math.min(s + 1, STEPS.length - 1));
  };
  const back = () => setStep((s) => Math.max(s - 1, 0));

  const saveProfile = async () => {
    setBusy(true);
    try {
      await api.put("/operators/profile", profile);
      await refresh();
      toast.success("Operator profile saved");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally { setBusy(false); }
  };

  const finish = async () => {
    await saveProfile();
    nav(`/operators/${user.id}`);
  };

  return (
    <div className="max-w-4xl mx-auto px-6 py-10">
      <SectionTitle
        overline="// OPERATOR ONBOARDING"
        title="Add your company & fleet."
        subtitle="List your aircraft, upload your D085 ops-specs, and start receiving charter quote requests."
      />

      {/* Progress bar */}
      <div className="mb-6">
        <div className="h-1 bg-white/[0.06] rounded-full overflow-hidden">
          <div className="h-full bg-cyan transition-all" style={{ width: `${((step + 1) / STEPS.length) * 100}%` }} />
        </div>
        <div className="mono text-[11px] text-white/50 mt-2">Step {step + 1} of {STEPS.length} — <span className="text-white/80">{STEPS[step]}</span></div>
      </div>

      <Card testId="operator-onboard">
        {step === 0 && (
          <div className="space-y-4">
            <Field label="Company / DBA Name *">
              <input data-testid="op-company" className="input" value={profile.company_name} onChange={(e) => setProfile({...profile, company_name: e.target.value})} placeholder="Skybound Aviation LLC" />
            </Field>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <Field label="Contact Email">
                <input data-testid="op-email" className="input" value={profile.contact_email} onChange={(e) => setProfile({...profile, contact_email: e.target.value})} placeholder="ops@yourco.com" />
              </Field>
              <Field label="Contact Phone">
                <input data-testid="op-phone" className="input mono" value={profile.contact_phone} onChange={(e) => setProfile({...profile, contact_phone: e.target.value})} placeholder="+1 (555) 555-5555" />
              </Field>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <Field label="Company Logo">
                <FileUpload testId="op-logo" kind="logo" accept="image/*" maxMB={5} value={profile.company_logo_file_id} onChange={(v) => setProfile({...profile, company_logo_file_id: v})} />
              </Field>
              <Field label="Hero Photo (fleet lineup)">
                <FileUpload testId="op-hero" kind="hero" accept="image/*" maxMB={15} value={profile.hero_photo_file_id} onChange={(v) => setProfile({...profile, hero_photo_file_id: v})} />
              </Field>
            </div>
          </div>
        )}

        {step === 1 && (
          <div className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <Field label="Part 135 Certificate # *">
                <input data-testid="op-cert" className="input mono" value={profile.part_135_certificate} onChange={(e) => setProfile({...profile, part_135_certificate: e.target.value.toUpperCase()})} placeholder="SKY135A" />
              </Field>
              <Field label="FAA Operator Designator">
                <input data-testid="op-designator" className="input mono" value={profile.faa_operator_id} onChange={(e) => setProfile({...profile, faa_operator_id: e.target.value.toUpperCase()})} placeholder="SKB" />
              </Field>
            </div>
            <Field label="D085 Ops-Specs (PDF)">
              <FileUpload testId="op-d085" kind="d085" accept="application/pdf" maxMB={20} value={profile.d085_file_id} onChange={(v) => setProfile({...profile, d085_file_id: v})} />
              <div className="mt-1 text-[11px] text-white/45">
                Your Operations Specifications (D085) confirms your authorized aircraft types, airports, and regions.
                Upload the PDF — PJP reviews it before flagging your profile as <span className="text-avail mono">D085-VERIFIED</span>.
              </div>
              {profile.d085_file_id && (
                <div className="mt-2 inline-flex items-center gap-2 text-[11px] text-avail mono">
                  <CheckCircle2 className="h-3.5 w-3.5" /> D085 uploaded — pending review
                </div>
              )}
            </Field>
          </div>
        )}

        {step === 2 && (
          <FleetEditor
            fleet={fleet}
            catalog={catalog}
            autoEditId={editAircraftId}
            autoNew={newAircraft}
            onReload={async () => {
              const r = await api.get("/operators/me/fleet");
              setFleet(r.data.fleet || []);
            }}
          />
        )}

        {step === 3 && (
          <div className="space-y-4">
            <Field label="Primary Bases (ICAO)">
              <MultiIcaoInput
                testId="op-bases"
                value={profile.primary_bases}
                onChange={(v) => setProfile({...profile, primary_bases: v})}
                placeholder="Add each base ICAO — KTEB, KVNY, KOPF…"
              />
            </Field>
            <Field label="Service Regions">
              <RegionMultiSelect
                testId="op-regions"
                value={profile.service_regions || []}
                onChange={(v) => setProfile({...profile, service_regions: v})}
              />
              <div className="mt-1 text-[11px] text-white/45">
                Select every region you can operate in. Saved to your profile — subsequently added aircraft inherit this list.
              </div>
            </Field>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <Field label="Hiring / Availability">
                <select
                  data-testid="op-hiring"
                  className="input"
                  value={HIRING.includes(profile.hiring_status) ? profile.hiring_status : HIRING[0]}
                  onChange={(e) => setProfile({...profile, hiring_status: e.target.value})}
                >
                  {HIRING.map((h) => <option key={h} value={h}>{h}</option>)}
                </select>
              </Field>
              <Field label="Safety Rating">
                <select data-testid="op-rating" className="input" value={profile.argus_wyvern_rating} onChange={(e) => setProfile({...profile, argus_wyvern_rating: e.target.value})}>
                  {RATINGS.map((r) => <option key={r} value={r}>{r}</option>)}
                </select>
              </Field>
            </div>
          </div>
        )}

        {step === 4 && (
          <div className="space-y-3">
            <div className="overline text-cyan">// REVIEW · READY TO GO LIVE?</div>
            <ReviewRow k="Company" v={profile.company_name} />
            <ReviewRow k="Cert #" v={<span className="mono">{profile.part_135_certificate}{profile.faa_operator_id ? ` · ${profile.faa_operator_id}` : ""}</span>} />
            <ReviewRow k="D085" v={profile.d085_file_id ? <span className="text-avail mono">Uploaded · Pending review</span> : <span className="text-orange-hud mono">Not uploaded</span>} />
            <ReviewRow k="Fleet" v={`${fleet.length} aircraft`} />
            <ReviewRow k="Bases" v={(profile.primary_bases || []).join(", ") || "—"} />
            <ReviewRow k="Regions" v={(profile.service_regions || []).join(", ") || "—"} />
            <ReviewRow k="Safety" v={profile.argus_wyvern_rating} />
          </div>
        )}

        <div className="flex justify-between mt-8 pt-5 border-t border-white/[0.06]">
          {step > 0 ? (
            <button onClick={back} data-testid="op-back" className="btn"><ArrowLeft className="h-3.5 w-3.5" /> Back</button>
          ) : (
            <div />
          )}
          {step < STEPS.length - 1 ? (
            <button onClick={async () => { if (step >= 0) { await saveProfile(); next(); } else { next(); } }} data-testid="op-next" className="btn btn-primary">
              {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <>Save & Continue <ArrowRight className="h-3.5 w-3.5" /></>}
            </button>
          ) : (
            <button onClick={finish} disabled={busy} data-testid="op-finish" className="btn btn-primary">
              {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <>GO LIVE <ArrowRight className="h-3.5 w-3.5" /></>}
            </button>
          )}
        </div>
      </Card>
    </div>
  );
}

/* ============ Fleet Editor ============ */
function FleetEditor({ fleet, catalog, onReload, autoEditId, autoNew }) {
  const [editing, setEditing] = useState(null); // aircraft or "new"
  const empty = { tail_number: "", type_code: "", year: "", home_base_icao: "", seats: "", range_nm: "",
                  interior_photos: [], exterior_photos: [], videos: [], charter_ready: true, notes: "",
                  adsb_hex_code: "", pjp_fuel_year: new Date().getFullYear(),
                  serial_number: "", hourly_rate_usd: "" };
  const [form, setForm] = useState(empty);
  const [busy, setBusy] = useState(false);

  const start = (ac) => {
    setForm(ac ? {
      ...empty, ...ac,
      year: ac.year || "",
      seats: ac.seats || "",
      range_nm: ac.range_nm || "",
      hourly_rate_usd: ac.hourly_rate_usd ?? "",
    } : empty);
    setEditing(ac ? ac.id : "new");
  };
  const cancel = () => { setEditing(null); setForm(empty); };

  // Deep-link support: /operators/onboard?step=fleet&edit=<id> auto-opens the editor for that
  // aircraft; ?step=fleet&new=1 auto-opens the "add aircraft" form.
  useEffect(() => {
    if (editing !== null) return;
    if (autoEditId && fleet.length) {
      const target = fleet.find((a) => a.id === autoEditId);
      if (target) start(target);
    } else if (autoNew) {
      start(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoEditId, autoNew, fleet]);

  const save = async () => {
    if (!form.tail_number || !form.type_code) return toast.error("Tail # + type required");
    setBusy(true);
    try {
      const payload = {
        ...form,
        tail_number: form.tail_number.toUpperCase(),
        year: form.year ? Number(form.year) : null,
        seats: form.seats ? Number(form.seats) : null,
        range_nm: form.range_nm ? Number(form.range_nm) : null,
        hourly_rate_usd: form.hourly_rate_usd ? Number(form.hourly_rate_usd) : null,
      };
      // Strip UI-only enrichment fields so the API model stays clean.
      delete payload.pjp_fuel_active;
      delete payload.pjp_fuel_expires_at;
      delete payload.pjp_fuel_type;
      delete payload.pjp_fuel_enrolled;  // legacy — no longer accepted server-side
      delete payload.id;
      delete payload.owner_id;
      delete payload.created_at;
      delete payload.updated_at;
      if (editing === "new") await api.post("/operators/aircraft", payload);
      else await api.put(`/operators/aircraft/${editing}`, payload);
      await onReload();
      cancel();
      toast.success("Fleet updated");
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
    finally { setBusy(false); }
  };

  const del = async (id) => {
    if (!window.confirm("Remove this aircraft from your fleet?")) return;
    try {
      await api.delete(`/operators/aircraft/${id}`);
      await onReload();
      toast.success("Aircraft removed");
    } catch (e) { toast.error(e?.response?.data?.detail || "Delete failed"); }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div className="overline text-cyan">// FLEET · {fleet.length} AIRCRAFT</div>
        {editing === null && (
          <button onClick={() => start(null)} data-testid="btn-add-aircraft" className="btn btn-primary">
            <Plane className="h-3.5 w-3.5" /> Add Aircraft
          </button>
        )}
      </div>

      {editing !== null ? (
        <div className="rounded-lg border border-cyan/30 bg-cyan/[0.03] p-4 space-y-3" data-testid="aircraft-form">
          <div className="grid grid-cols-2 gap-3">
            <Field label="Tail Number *">
              <input data-testid="ac-tail" className="input mono uppercase" value={form.tail_number} onChange={(e) => setForm({...form, tail_number: e.target.value.toUpperCase()})} placeholder="N550SB" />
            </Field>
            <Field label="Type *">
              <select data-testid="ac-type" className="input" value={form.type_code} onChange={(e) => setForm({...form, type_code: e.target.value})}>
                <option value="">Select type…</option>
                {Object.entries(catalog)
                  .sort(([a], [b]) => a.localeCompare(b))
                  .map(([oem, list]) => (
                    <optgroup key={oem} label={oem}>
                      {[...list]
                        .sort((a, b) => a.label.localeCompare(b.label))
                        .map((t) => <option key={t.code} value={t.code}>{t.label}</option>)}
                    </optgroup>
                  ))}
              </select>
            </Field>
          </div>
          <div className="grid grid-cols-3 gap-3">
            <Field label="Year"><input data-testid="ac-year" type="number" className="input" value={form.year} onChange={(e) => setForm({...form, year: e.target.value})} placeholder="2019" /></Field>
            <Field label="Seats"><input data-testid="ac-seats" type="number" className="input" value={form.seats} onChange={(e) => setForm({...form, seats: e.target.value})} placeholder="14" /></Field>
            <Field label="Range (NM)"><input data-testid="ac-range" type="number" className="input" value={form.range_nm} onChange={(e) => setForm({...form, range_nm: e.target.value})} placeholder="6500" /></Field>
          </div>
          <Field label="Home Base">
            <AirportInput testId="ac-home" value={form.home_base_icao} onChange={(v) => setForm({...form, home_base_icao: v})} placeholder="Airport where the plane lives" />
          </Field>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <Field label="Exterior Photos">
              <FileUpload testId="ac-ext" kind="aircraft_exterior" accept="image/*" maxMB={15} multiple value={form.exterior_photos} onChange={(v) => setForm({...form, exterior_photos: v})} variant="gallery" />
            </Field>
            <Field label="Interior Photos">
              <FileUpload testId="ac-int" kind="aircraft_interior" accept="image/*" maxMB={15} multiple value={form.interior_photos} onChange={(v) => setForm({...form, interior_photos: v})} variant="gallery" />
            </Field>
          </div>
          <Field label="Videos (walk-around / cabin tour)">
            <FileUpload testId="ac-vid" kind="aircraft_video" accept="video/mp4,video/quicktime,video/webm" maxMB={200} multiple value={form.videos} onChange={(v) => setForm({...form, videos: v})} variant="gallery" />
          </Field>
          <Field label="Notes">
            <textarea data-testid="ac-notes" className="input" rows={3} value={form.notes} onChange={(e) => setForm({...form, notes: e.target.value})} placeholder="Wifi, catering partners, min-age requirements, availability windows…" />
          </Field>

          {/* Live tracking + billable rate + fuel program status */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3 pt-3 border-t border-white/[0.06]">
            <Field label="Serial #">
              <input data-testid="ac-serial" className="input" value={form.serial_number || ""} onChange={(e) => setForm({...form, serial_number: e.target.value})} placeholder="5123" />
            </Field>
            <Field label="ICAO 24-bit address (Mode S hex)">
              <input data-testid="ac-adsb" className="input mono uppercase" value={form.adsb_hex_code || ""} onChange={(e) => setForm({...form, adsb_hex_code: e.target.value.toUpperCase().slice(0,6)})} placeholder="A12B34" maxLength={6} />
              <div className="text-[10px] text-white/50 mt-1 leading-relaxed">
                <span className="text-white/70">Where do I find this?</span> Look up your tail number in the{" "}
                <a
                  href="https://registry.faa.gov/AircraftInquiry/Search/NNumberInquiry"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-cyan hover:underline"
                  data-testid="ac-adsb-faa-link"
                >FAA Aircraft Registry ↗</a>
                , open the record, then copy the six-character{" "}
                <span className="mono text-white/70">Mode S Code (Base 16 / Hex)</span> — e.g. <span className="mono text-cyan">A12B34</span>. Do <span className="text-orange-hud">not</span> use the Base 8 / Octal value.
              </div>
            </Field>
            <Field label="Hourly billable rate (USD)">
              <div className="relative">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-white/40 mono text-sm pointer-events-none">$</span>
                <input
                  data-testid="ac-hourly-rate"
                  type="number"
                  min="0"
                  step="50"
                  className="input mono pl-6"
                  value={form.hourly_rate_usd ?? ""}
                  onChange={(e) => setForm({...form, hourly_rate_usd: e.target.value})}
                  placeholder="7500"
                />
              </div>
              <div className="text-[10px] text-white/50 mt-1">
                Powers PJP's <span className="text-cyan">auto-quote engine</span> — used with type speed + city-pair flight time to instantly quote visitors.
              </div>
            </Field>
          </div>

          {/* PJP Fuel — paid membership status. Toggle is READ-ONLY; enrollment is a paid purchase. */}
          <div className="mt-3 rounded-lg border border-white/[0.06] bg-black/30 p-3" data-testid="ac-fuel-status">
            {editing !== "new" && form.pjp_fuel_active ? (
              <div className="flex items-center justify-between gap-3">
                <div>
                  <div className="mono text-[10px] tracking-widest text-avail">// PJP FUEL · ACTIVE</div>
                  <div className="text-xs text-white/70 mt-0.5">
                    {form.pjp_fuel_type ? `${form.pjp_fuel_type.toUpperCase()} · ` : ""}
                    Expires {form.pjp_fuel_expires_at ? new Date(form.pjp_fuel_expires_at).toLocaleDateString() : "—"}
                  </div>
                </div>
                <span className="mono text-[10px] px-2 py-0.5 rounded-full border border-avail/40 bg-avail/10 text-avail">PAID</span>
              </div>
            ) : (
              <div className="flex items-center justify-between gap-3 flex-wrap">
                <div>
                  <div className="mono text-[10px] tracking-widest text-white/60">// PJP FUEL · NOT ENROLLED</div>
                  <div className="text-xs text-white/60 mt-0.5">
                    Unlock PJP-negotiated fuel pricing for this tail at every participating FBO — <span className="text-cyan mono">$500 / yr</span>.
                  </div>
                </div>
                <a
                  href={`/fuel?tail=${encodeURIComponent(form.tail_number || "")}`}
                  data-testid="ac-fuel-enroll-link"
                  className="btn"
                >
                  Enroll in PJP Fuel →
                </a>
              </div>
            )}
          </div>
          <label className="flex items-center gap-2 text-sm mono text-white/70 mt-2">
            <input type="checkbox" checked={form.charter_ready} onChange={(e) => setForm({...form, charter_ready: e.target.checked})} />
            Currently available for charter requests
          </label>
          <div className="flex gap-2 justify-end pt-2">
            <button onClick={cancel} className="btn">Cancel</button>
            <button onClick={save} disabled={busy} data-testid="btn-save-aircraft" className="btn btn-primary">
              {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : "Save aircraft"}
            </button>
          </div>
        </div>
      ) : (
        <div className="grid gap-3">
          {fleet.length === 0 && (
            <div className="text-center py-8 text-white/50 border border-dashed border-white/10 rounded-lg">
              No aircraft yet. Click <span className="text-cyan mono">Add Aircraft</span> to add your first plane.
            </div>
          )}
          {fleet.map((ac) => (
            <div key={ac.id} className="flex items-center gap-3 p-3 rounded-lg border border-white/10 bg-black/30" data-testid={`ac-row-${ac.tail_number}`}>
              {ac.exterior_photos?.[0] ? (
                <img src={`/api/files/${ac.exterior_photos[0]}`} alt="" className="h-16 w-16 object-cover rounded-md" />
              ) : (
                <div className="h-16 w-16 rounded-md bg-white/[0.03] flex items-center justify-center text-white/40"><Plane className="h-6 w-6" /></div>
              )}
              <div className="flex-1">
                <div className="mono text-sm font-bold text-cyan">{ac.tail_number} · {ac.type_code}</div>
                <div className="text-xs text-white/60 mt-0.5">{ac.year ? `${ac.year} · ` : ""}{ac.seats ? `${ac.seats} seats · ` : ""}{ac.range_nm ? `${ac.range_nm} NM · ` : ""}{ac.home_base_icao || "no base"}</div>
              </div>
              <button onClick={() => start(ac)} className="btn"><Edit3 className="h-3 w-3" /> Edit</button>
              <button onClick={() => del(ac.id)} className="btn"><Trash2 className="h-3 w-3" /></button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

/* ============ Region multi-select ============ */
function RegionMultiSelect({ value = [], onChange, testId }) {
  const toggle = (region) => {
    const has = value.includes(region);
    // "World Wide Ops" acts as a super-set — selecting it clears others; selecting anything else clears WWO.
    if (region === "World Wide Ops") {
      onChange(has ? [] : ["World Wide Ops"]);
      return;
    }
    const withoutWWO = value.filter((v) => v !== "World Wide Ops");
    onChange(has ? withoutWWO.filter((v) => v !== region) : [...withoutWWO, region]);
  };
  return (
    <div className="flex flex-wrap gap-1.5" data-testid={testId}>
      {SERVICE_REGIONS.map((r) => {
        const selected = value.includes(r);
        const isWWO = r === "World Wide Ops";
        return (
          <button
            key={r}
            type="button"
            onClick={() => toggle(r)}
            data-testid={`region-chip-${r.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
            className={`inline-flex items-center gap-1.5 pl-3 pr-3 py-1.5 rounded-full text-[11px] mono font-semibold border transition-colors ${
              selected
                ? isWWO
                  ? "border-orange-hud/60 bg-orange-hud/[0.15] text-orange-hud"
                  : "border-cyan/60 bg-cyan/[0.15] text-cyan"
                : "border-white/15 bg-white/[0.03] text-white/60 hover:border-white/30 hover:text-white/90"
            }`}
          >
            {selected && <span className="text-[9px]">✓</span>}
            {r.toUpperCase()}
          </button>
        );
      })}
    </div>
  );
}

/* ============ Multi-ICAO chip input (for bases) ============ */
function MultiIcaoInput({ value = [], onChange, placeholder, testId }) {
  const [text, setText] = useState("");
  const add = () => {
    const codes = text.split(/[\s,\-]+/).map((s) => s.trim().toUpperCase()).filter((s) => s.length >= 3 && s.length <= 4);
    if (codes.length) { onChange([...value, ...codes]); setText(""); }
  };
  const remove = (i) => { const n = [...value]; n.splice(i, 1); onChange(n); };
  return (
    <div className="rounded-lg border border-cyan/25 bg-black/30 p-2 min-h-[52px] flex flex-wrap gap-1.5 items-center">
      {value.map((c, i) => (
        <span key={i} className="inline-flex items-center gap-1 pl-2 pr-1 py-0.5 rounded-full text-[11px] mono font-semibold border border-cyan/40 bg-cyan/[0.08] text-cyan">
          {c}
          <button type="button" onClick={() => remove(i)} className="opacity-70 hover:opacity-100">×</button>
        </span>
      ))}
      <input
        data-testid={testId}
        className="flex-1 bg-transparent outline-none text-sm mono px-1 min-w-[160px]"
        value={text}
        onChange={(e) => setText(e.target.value)}
        onKeyDown={(e) => { if (e.key === "Enter" || e.key === ",") { e.preventDefault(); add(); } }}
        placeholder={value.length ? "Add another ICAO…" : placeholder}
      />
    </div>
  );
}

function ReviewRow({ k, v }) {
  return (
    <div className="flex gap-4 py-2 border-b border-white/[0.05] last:border-b-0">
      <div className="w-24 eyebrow-muted uppercase">{k}</div>
      <div className="flex-1 text-white/90">{v}</div>
    </div>
  );
}


/* ---------------- Entity Signup (Charter Operator account creation) ---------------- */

function OperatorEntitySignup({ onDone }) {
  const { register: doRegister, login } = useAuth();
  const [form, setForm] = useState({
    company_name: "",
    contact_first_name: "",
    contact_last_name: "",
    email: "",
    password: "",
    confirm: "",
    part_135_certificate: "",
  });
  const [busy, setBusy] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.company_name.trim()) return toast.error("Company name required");
    if (!form.email.trim()) return toast.error("Business email required");
    if (form.password.length < 6) return toast.error("Password must be at least 6 characters");
    if (form.password !== form.confirm) return toast.error("Passwords don't match");
    setBusy(true);
    try {
      // 1. Create the entity user account (role = charter_operator, primary hat).
      await doRegister({
        first_name: form.contact_first_name || form.company_name,
        last_name: form.contact_last_name || "Operations",
        email: form.email,
        password: form.password,
        role: "charter_operator",
      });
      // 2. Log in (register auto-logs in via AuthContext, but be defensive).
      try { await login(form.email, form.password); } catch {}
      // 3. Persist initial operator profile so the wizard starts pre-filled.
      try {
        await api.put("/operators/profile", {
          company_name: form.company_name,
          part_135_certificate: form.part_135_certificate || "PENDING",
          contact_email: form.email,
        });
      } catch {}
      toast.success("Operator account created");
      onDone && onDone();
    } catch (e) {
      toast.error(e?.response?.data?.detail || e?.message || "Sign-up failed");
    } finally { setBusy(false); }
  };

  return (
    <Card data-testid="operator-entity-signup">
      <div className="overline text-cyan mb-2">// CREATE OPERATOR ACCOUNT</div>
      <h2 className="font-display text-2xl font-bold tracking-tight mb-2">Register your charter company.</h2>
      <p className="text-sm text-white/55 mb-6">
        This creates a dedicated business account for your company's fleet, certifications, and Acrisure paperwork —
        completely separate from your personal PJP profile. Sign in and out between them anytime.
      </p>
      <form onSubmit={submit} className="space-y-4">
        <Field label="Company name *">
          <input data-testid="entity-company-name" className="input-hud" value={form.company_name} onChange={(e) => set("company_name", e.target.value)} placeholder="Integra Jet Management" />
        </Field>
        <div className="grid md:grid-cols-2 gap-3">
          <Field label="Contact first name">
            <input data-testid="entity-first" className="input-hud" value={form.contact_first_name} onChange={(e) => set("contact_first_name", e.target.value)} placeholder="Jeff" />
          </Field>
          <Field label="Contact last name">
            <input data-testid="entity-last" className="input-hud" value={form.contact_last_name} onChange={(e) => set("contact_last_name", e.target.value)} placeholder="Coursey" />
          </Field>
        </div>
        <Field label="Business email *">
          <input data-testid="entity-email" type="email" className="input-hud" value={form.email} onChange={(e) => set("email", e.target.value)} placeholder="ops@yourcompany.com" />
        </Field>
        <div className="grid md:grid-cols-2 gap-3">
          <Field label="Password *">
            <PasswordInput data-testid="entity-password" className="input-hud" value={form.password} onChange={(e) => set("password", e.target.value)} autoComplete="new-password" minLength={6} />
          </Field>
          <Field label="Confirm password *">
            <PasswordInput data-testid="entity-password-confirm" className="input-hud" value={form.confirm} onChange={(e) => set("confirm", e.target.value)} autoComplete="new-password" minLength={6} />
          </Field>
        </div>
        <Field label="Part 135 Certificate # (optional now — fill in the wizard)">
          <input data-testid="entity-part135" className="input-hud" value={form.part_135_certificate} onChange={(e) => set("part_135_certificate", e.target.value)} placeholder="XX0A123B" />
        </Field>
        <button
          data-testid="entity-submit"
          type="submit"
          disabled={busy}
          className="btn btn-primary w-full justify-center"
        >
          {busy ? <><Loader2 className="h-3.5 w-3.5 animate-spin" /> Creating…</> : <>Create Operator Account <ArrowRight className="h-3.5 w-3.5" /></>}
        </button>
      </form>
    </Card>
  );
}
